// File utility functions for chat media handling

/**
 * Format file size in human readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Get file extension from filename
 * @param {string} filename - File name
 * @returns {string} File extension
 */
export const getFileExtension = (filename) => {
  return filename?.split('.').pop()?.toLowerCase() || '';
};

/**
 * Check if file is an image
 * @param {File|string} file - File object or filename
 * @returns {boolean} True if file is an image
 */
export const isImageFile = (file) => {
  if (typeof file === 'string') {
    const ext = getFileExtension(file);
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext);
  }
  return file?.type?.startsWith('image/') || false;
};

/**
 * Check if file is a video
 * @param {File|string} file - File object or filename
 * @returns {boolean} True if file is a video
 */
export const isVideoFile = (file) => {
  if (typeof file === 'string') {
    const ext = getFileExtension(file);
    return ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'].includes(ext);
  }
  return file?.type?.startsWith('video/') || false;
};

/**
 * Check if file is an audio file
 * @param {File|string} file - File object or filename
 * @returns {boolean} True if file is an audio file
 */
export const isAudioFile = (file) => {
  if (typeof file === 'string') {
    const ext = getFileExtension(file);
    return ['mp3', 'wav', 'ogg', 'm4a', 'aac', 'flac', 'webm'].includes(ext);
  }
  return file?.type?.startsWith('audio/') || false;
};

/**
 * Check if file is a document
 * @param {File|string} file - File object or filename
 * @returns {boolean} True if file is a document
 */
export const isDocumentFile = (file) => {
  if (typeof file === 'string') {
    const ext = getFileExtension(file);
    return ['pdf', 'doc', 'docx', 'txt', 'rtf', 'odt'].includes(ext);
  }
  return file?.type?.includes('document') || file?.type?.includes('pdf') || false;
};

/**
 * Check if file is a spreadsheet
 * @param {File|string} file - File object or filename
 * @returns {boolean} True if file is a spreadsheet
 */
export const isSpreadsheetFile = (file) => {
  if (typeof file === 'string') {
    const ext = getFileExtension(file);
    return ['xls', 'xlsx', 'csv', 'ods'].includes(ext);
  }
  return file?.type?.includes('spreadsheet') || file?.type?.includes('excel') || false;
};

/**
 * Check if file is a presentation
 * @param {File|string} file - File object or filename
 * @returns {boolean} True if file is a presentation
 */
export const isPresentationFile = (file) => {
  if (typeof file === 'string') {
    const ext = getFileExtension(file);
    return ['ppt', 'pptx', 'odp'].includes(ext);
  }
  return file?.type?.includes('presentation') || file?.type?.includes('powerpoint') || false;
};

/**
 * Get file type category
 * @param {File|string} file - File object or filename
 * @returns {string} File type category
 */
export const getFileTypeCategory = (file) => {
  if (isImageFile(file)) return 'image';
  if (isVideoFile(file)) return 'video';
  if (isAudioFile(file)) return 'audio';
  if (isDocumentFile(file)) return 'document';
  if (isSpreadsheetFile(file)) return 'spreadsheet';
  if (isPresentationFile(file)) return 'presentation';
  return 'other';
};

/**
 * Validate file size
 * @param {File} file - File object
 * @param {number} maxSizeMB - Maximum size in MB
 * @returns {boolean} True if file size is valid
 */
export const validateFileSize = (file, maxSizeMB = 10) => {
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  return file.size <= maxSizeBytes;
};

/**
 * Validate file type
 * @param {File} file - File object
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @returns {boolean} True if file type is allowed
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (allowedTypes.length === 0) return true;
  return allowedTypes.includes(file.type);
};

/**
 * Download file from URL
 * @param {string} url - File URL
 * @param {string} filename - Filename for download
 */
export const downloadFile = (url, filename) => {
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.target = '_blank';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Open file in new tab
 * @param {string} url - File URL
 */
export const openFileInNewTab = (url) => {
  window.open(url, '_blank');
};

/**
 * Create object URL for file preview
 * @param {File} file - File object
 * @returns {string} Object URL
 */
export const createObjectURL = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Revoke object URL to free memory
 * @param {string} url - Object URL
 */
export const revokeObjectURL = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

/**
 * Get file icon based on type
 * @param {File|string} file - File object or filename
 * @returns {string} Icon name
 */
export const getFileIcon = (file) => {
  const category = getFileTypeCategory(file);
  
  switch (category) {
    case 'image':
      return 'image';
    case 'video':
      return 'video';
    case 'audio':
      return 'music';
    case 'document':
      return 'file-text';
    case 'spreadsheet':
      return 'file-spreadsheet';
    case 'presentation':
      return 'file-presentation';
    default:
      return 'file';
  }
};

/**
 * Generate thumbnail for image file
 * @param {File} file - Image file
 * @param {number} maxWidth - Maximum width
 * @param {number} maxHeight - Maximum height
 * @returns {Promise<string>} Thumbnail data URL
 */
export const generateImageThumbnail = (file, maxWidth = 200, maxHeight = 200) => {
  return new Promise((resolve, reject) => {
    if (!isImageFile(file)) {
      reject(new Error('File is not an image'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      // Calculate new dimensions
      let { width, height } = img;
      
      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw image
      ctx.drawImage(img, 0, 0, width, height);

      // Convert to data URL
      const dataURL = canvas.toDataURL('image/jpeg', 0.8);
      resolve(dataURL);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Compress image file
 * @param {File} file - Image file
 * @param {number} quality - Compression quality (0-1)
 * @returns {Promise<File>} Compressed file
 */
export const compressImage = (file, quality = 0.8) => {
  return new Promise((resolve, reject) => {
    if (!isImageFile(file)) {
      reject(new Error('File is not an image'));
      return;
    }

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;

      // Draw image
      ctx.drawImage(img, 0, 0);

      // Convert to blob
      canvas.toBlob(
        (blob) => {
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        file.type,
        quality
      );
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    img.src = URL.createObjectURL(file);
  });
};

/**
 * Get file info for display
 * @param {File} file - File object
 * @returns {object} File info object
 */
export const getFileInfo = (file) => {
  return {
    name: file.name,
    size: formatFileSize(file.size),
    type: file.type,
    category: getFileTypeCategory(file),
    icon: getFileIcon(file),
    extension: getFileExtension(file.name)
  };
};

/**
 * Validate file for upload
 * @param {File} file - File object
 * @param {object} options - Validation options
 * @returns {object} Validation result
 */
export const validateFileForUpload = (file, options = {}) => {
  const {
    maxSizeMB = 10,
    allowedTypes = [],
    allowedExtensions = []
  } = options;

  const errors = [];

  // Check file size
  if (!validateFileSize(file, maxSizeMB)) {
    errors.push(`File size must be less than ${maxSizeMB}MB`);
  }

  // Check file type
  if (allowedTypes.length > 0 && !validateFileType(file, allowedTypes)) {
    errors.push('File type not allowed');
  }

  // Check file extension
  if (allowedExtensions.length > 0) {
    const ext = getFileExtension(file.name);
    if (!allowedExtensions.includes(ext)) {
      errors.push('File extension not allowed');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
